import { Box, IconButton, Paper, Typography } from '@mui/material';
import clsx from 'clsx';
import type React from 'react';

import Close from '~/assets/icons/new/close.svg?react';
import ErrorIcon from '~/assets/icons/new/error.svg?react';
import InfoIcon from '~/assets/icons/new/infoIcon.svg?react';
import WarningIcon from '~/assets/icons/new/warning.svg?react';
import Tick from '~/assets/icons/tickIcon.svg?react';

import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';

import useStyles from './ToastMessage.styles';

export enum TOAST_STATUS {
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
}

interface ToastMessageProps {
  message: string;
  status: TOAST_STATUS;
  testId: string;
  onClose: () => void;
}

const TOAST_STATUS_ICON = {
  [TOAST_STATUS.INFO]: <InfoIcon data-testid="toast-info-icon" />,
  [TOAST_STATUS.WARNING]: <WarningIcon data-testid="toast-warning-icon" />,
  [TOAST_STATUS.ERROR]: <ErrorIcon data-testid="toast-error-icon" />,
  [TOAST_STATUS.SUCCESS]: <Tick data-testid="toast-success-icon" />,
};

export const ToastMessage: React.FC<ToastMessageProps> = ({
  message,
  status,
  testId,
  onClose,
}) => {
  const { classes } = useStyles();

  return (
    <Paper
      elevation={6}
      className={clsx(`toast-${status}`, classes.toastMessage)}
      data-testid={testId}
      role={status === TOAST_STATUS.INFO ? 'status' : 'alert'}
    >
      {TOAST_STATUS_ICON[status]}
      <Box ml={1} flex={1}>
        <Typography variant="body_medium" data-testid="toast-label">
          {message}
        </Typography>
      </Box>
      <IconButton
        aria-label={translateText('common:toast:aria-labels.dismiss')}
        color="inherit"
        className={classes.toastIconCloseButton}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClose();
          }
        }}
        data-testid="toast-message-close"
        size="small"
        tabIndex={0}
      >
        <Close
          className={classes.toastIconCloseButtonIcon}
          data-testid="toast-close-icon"
          aria-hidden="true"
        />
      </IconButton>
    </Paper>
  );
};

export default ToastMessage;
